/**
 * خدمة نماذج اللغة الكبيرة (LLM) — تحسين البرومبت + فلترة المحتوى
 * يدعم: OpenRouter (مجاني), Groq, Gemini, Cohere, Mistral, HuggingFace, مخصص
 */

// === قائمة الكلمات المحظورة (عربي + إنجليزي) ===
const BANNED_KEYWORDS_AR = [
    'عري', 'عارية', 'عاري', 'جنس', 'جنسي', 'جنسية', 'إباحي', 'إباحية', 'بورنو',
    'اغتصاب', 'تحرش', 'قتل', 'ذبح', 'دم', 'دماء', 'انتحار', 'مخدرات', 'حشيش',
    'كوكايين', 'هيروين', 'إرهاب', 'إرهابي', 'تفجير', 'قنبلة', 'سلاح', 'بندقية',
    'عنصرية', 'عنصري', 'نازي', 'هتلر', 'داعش', 'القاعدة',
    'طفل عاري', 'أطفال عراة', 'استغلال أطفال',
];

const BANNED_KEYWORDS_EN = [
    'nude', 'naked', 'nsfw', 'porn', 'pornograph', 'sexual', 'erotic', 'hentai',
    'rape', 'molest', 'gore', 'murder', 'suicide', 'drug', 'cocaine', 'heroin',
    'terrorist', 'terrorism', 'bomb', 'weapon', 'nazi', 'hitler', 'isis',
    'child abuse', 'child nude', 'underage',
    'kill', 'blood', 'decapitat', 'dismember', 'torture',
];

// === فلترة المحتوى بالكلمات ===
export function checkContentSafety(text) {
    if (!text || !text.trim()) return { safe: true, reason: '' };
    const lower = text.toLowerCase().trim();

    for (const kw of BANNED_KEYWORDS_AR) {
        if (lower.includes(kw)) {
            return { safe: false, reason: 'يحتوي على محتوى محظور: ' + kw };
        }
    }
    for (const kw of BANNED_KEYWORDS_EN) {
        if (lower.includes(kw)) {
            return { safe: false, reason: 'Contains prohibited content: ' + kw };
        }
    }
    return { safe: true, reason: '' };
}

// === System Prompt الافتراضي ===
export const DEFAULT_SYSTEM_PROMPT = `You are a professional AI image generation prompt engineer. Your tasks:
1. Take the user's description (in any language, often Arabic) and convert it to a detailed, structured English prompt for AI image generation.
2. Check for harmful content. If the request contains violence, nudity, sexual content, hate speech, real people's likenesses, or copyrighted characters, respond with EXACTLY: "BLOCKED: [reason in Arabic]"
3. Output ONLY the English prompt (50-150 words), nothing else. No explanations, no labels, no prefix.

Rules:
- Translate the concept, not word-by-word
- Add artistic details: lighting, composition, style, medium, color palette
- If the request is vague, add creative details to make a beautiful image
- Keep professional quality suitable for an art gallery
- Never include text/watermarks in the prompt`;

// === مزودي LLM الافتراضيون (مجانيون — لا يحتاجون مفتاح) ===
export const DEFAULT_LLM_PROVIDERS = [
    {
        id: 'openrouter-gemma-3-27b',
        type: 'openrouter',
        name: 'Gemma 3 27B',
        baseUrl: 'https://openrouter.ai/api/v1',
        model: 'google/gemma-3-27b-it:free',
        requiresKey: true,
        enabled: true,
        order: 0,
    },
    {
        id: 'openrouter-nemotron-nano',
        type: 'openrouter',
        name: 'Nemotron 3 Nano',
        baseUrl: 'https://openrouter.ai/api/v1',
        model: 'nvidia/nemotron-3-nano-30b-a3b:free',
        requiresKey: true,
        enabled: true,
        order: 1,
    },
    {
        id: 'openrouter-gemma-3-12b',
        type: 'openrouter',
        name: 'Gemma 3 12B',
        baseUrl: 'https://openrouter.ai/api/v1',
        model: 'google/gemma-3-12b-it:free',
        requiresKey: true,
        enabled: true,
        order: 2,
    },
    {
        id: 'openrouter-hermes-405b',
        type: 'openrouter',
        name: 'Hermes 3 405B',
        baseUrl: 'https://openrouter.ai/api/v1',
        model: 'nousresearch/hermes-3-llama-3.1-405b:free',
        requiresKey: true,
        enabled: true,
        order: 3,
    },
];

const normalizeBaseUrl = (url) => String(url || '').trim().replace(/\/+$/, '');
const joinUrl = (baseUrl, path) => normalizeBaseUrl(baseUrl) + '/' + String(path || '').replace(/^\/+/, '');

// === استدعاء API بصيغة OpenAI-compatible ===
async function callOpenAICompatible(baseUrl, model, apiKey, messages, signal) {
    const headers = { 'Content-Type': 'application/json' };
    if (apiKey) headers['Authorization'] = 'Bearer ' + apiKey;
    // OpenRouter specific headers
    if (baseUrl.includes('openrouter.ai')) {
        headers['HTTP-Referer'] = window.location.origin;
        headers['X-Title'] = 'AI Gallery';
    }

    let res;
    try {
        res = await fetch(joinUrl(baseUrl, 'chat/completions'), {
            method: 'POST',
            headers,
            body: JSON.stringify({
                model,
                messages,
                max_tokens: 500,
                temperature: 0.7,
            }),
            signal,
        });
    } catch (e) {
        throw new Error('تعذر الاتصال بالخدمة (CORS/Network). تأكد من Base URL بدون / في النهاية.');
    }

    if (!res.ok) {
        const errText = await res.text().catch(() => '');
        throw new Error('HTTP ' + res.status + ': ' + errText.slice(0, 200));
    }

    const data = await res.json();
    const content = data.choices?.[0]?.message?.content;
    if (!content) throw new Error('لا يوجد رد من النموذج');
    return content.trim();
}

// === استدعاء Google Gemini ===
async function callGemini(apiKey, model, messages, signal) {
    const modelName = model || 'gemini-2.0-flash';
    const url = 'https://generativelanguage.googleapis.com/v1beta/models/' + modelName + ':generateContent?key=' + apiKey;

    // تحويل messages إلى صيغة Gemini
    const contents = [];
    let systemInstruction = null;
    for (const msg of messages) {
        if (msg.role === 'system') {
            systemInstruction = { parts: [{ text: msg.content }] };
        } else {
            contents.push({
                role: msg.role === 'assistant' ? 'model' : 'user',
                parts: [{ text: msg.content }],
            });
        }
    }

    const body = { contents };
    if (systemInstruction) body.systemInstruction = systemInstruction;

    const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal,
    });

    if (!res.ok) {
        const errText = await res.text().catch(() => '');
        throw new Error('Gemini HTTP ' + res.status + ': ' + errText.slice(0, 200));
    }

    const data = await res.json();
    const content = data.candidates?.[0]?.content?.parts?.[0]?.text;
    if (!content) throw new Error('لا يوجد رد من Gemini');
    return content.trim();
}

// === استدعاء Cohere ===
async function callCohere(apiKey, model, messages, signal) {
    // تحويل messages إلى صيغة Cohere v2
    const cohereMessages = [];
    for (const msg of messages) {
        if (msg.role === 'system') {
            cohereMessages.push({ role: 'system', content: msg.content });
        } else {
            cohereMessages.push({
                role: msg.role === 'user' ? 'user' : 'assistant',
                content: msg.content,
            });
        }
    }

    const res = await fetch('https://api.cohere.com/v2/chat', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + apiKey,
        },
        body: JSON.stringify({
            model: model || 'command-r',
            messages: cohereMessages,
            max_tokens: 500,
        }),
        signal,
    });

    if (!res.ok) {
        const errText = await res.text().catch(() => '');
        throw new Error('Cohere HTTP ' + res.status + ': ' + errText.slice(0, 200));
    }

    const data = await res.json();
    const content = data.message?.content?.[0]?.text;
    if (!content) throw new Error('لا يوجد رد من Cohere');
    return content.trim();
}

// === استدعاء Anthropic (Claude) ===
async function callAnthropic(apiKey, model, messages, signal, baseUrl) {
    let systemText = '';
    const apiMessages = [];
    for (const msg of messages) {
        if (msg.role === 'system') {
            systemText = msg.content;
        } else {
            apiMessages.push({ role: msg.role, content: msg.content });
        }
    }

    const body = {
        model: model || 'claude-sonnet-4-20250514',
        max_tokens: 500,
        messages: apiMessages,
    };
    if (systemText) body.system = systemText;

    const cleanBaseUrl = normalizeBaseUrl(baseUrl || 'https://api.anthropic.com');

    // بعض المزودات (مثل BlazeAI) تعمل بصيغة OpenAI-compatible
    const seemsOpenAICompat = cleanBaseUrl.includes('boxu.dev') || cleanBaseUrl.endsWith('/api') || cleanBaseUrl.includes('/api/');
    if (baseUrl && seemsOpenAICompat) {
        try {
            return await callOpenAICompatible(cleanBaseUrl, model, apiKey, messages, signal);
        } catch (fallbackErr) {
            console.warn('OpenAI-compatible fallback failed, trying Anthropic endpoint:', fallbackErr);
        }
    }

    let res;
    try {
        res = await fetch(joinUrl(cleanBaseUrl, 'v1/messages'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-api-key': apiKey,
                'anthropic-version': '2023-06-01',
                'anthropic-dangerous-direct-browser-access': 'true',
            },
            body: JSON.stringify(body),
            signal,
        });
    } catch (e) {
        throw new Error('تعذر الاتصال بمزود Anthropic (CORS/Network). جرب Base URL بدون / في النهاية أو اختر Custom (OpenAI-compatible).');
    }

    if (!res.ok) {
        const errText = await res.text().catch(() => '');
        throw new Error('Anthropic HTTP ' + res.status + ': ' + errText.slice(0, 200));
    }

    const data = await res.json();
    const content = data.content?.[0]?.text;
    if (!content) throw new Error('لا يوجد رد من Anthropic');
    return content.trim();
}

// === جلب قائمة النماذج المتاحة من المزود ===
export async function fetchModels(type, apiKey, baseUrl) {
    try {
        switch (type) {
            case 'openrouter': {
                const res = await fetch('https://openrouter.ai/api/v1/models');
                if (!res.ok) throw new Error('HTTP ' + res.status);
                const data = await res.json();
                return (data.data || []).map(m => ({
                    id: m.id,
                    name: m.name || m.id,
                    free: m.id.endsWith(':free') || (m.pricing?.prompt === '0' && m.pricing?.completion === '0'),
                    context: m.context_length,
                })).sort((a, b) => (b.free ? 1 : 0) - (a.free ? 1 : 0) || a.name.localeCompare(b.name));
            }
            case 'groq': {
                if (!apiKey) return [];
                const res = await fetch('https://api.groq.com/openai/v1/models', {
                    headers: { 'Authorization': 'Bearer ' + apiKey },
                });
                if (!res.ok) throw new Error('HTTP ' + res.status);
                const data = await res.json();
                return (data.data || []).map(m => ({ id: m.id, name: m.id }));
            }
            case 'anthropic': {
                const anthropicFallback = [
                    { id: 'claude-sonnet-4-20250514', name: 'Claude Sonnet 4' },
                    { id: 'claude-3-5-haiku-20241022', name: 'Claude 3.5 Haiku' },
                    { id: 'claude-3-5-sonnet-20241022', name: 'Claude 3.5 Sonnet' },
                    { id: 'claude-3-haiku-20240307', name: 'Claude 3 Haiku' },
                ];
                if (!apiKey) return anthropicFallback;
                try {
                    const res = await fetch('https://api.anthropic.com/v1/models', {
                        headers: {
                            'x-api-key': apiKey,
                            'anthropic-version': '2023-06-01',
                            'anthropic-dangerous-direct-browser-access': 'true',
                        },
                    });
                    if (!res.ok) return anthropicFallback;
                    const data = await res.json();
                    const list = (data.data || []).map(m => ({ id: m.id, name: m.display_name || m.id }));
                    return list.length > 0 ? list : anthropicFallback;
                } catch (e) { return anthropicFallback; }
            }
            case 'gemini': {
                const geminiFallback = [
                    { id: 'gemini-2.0-flash', name: 'Gemini 2.0 Flash' },
                    { id: 'gemini-2.0-flash-lite', name: 'Gemini 2.0 Flash Lite' },
                    { id: 'gemini-1.5-flash', name: 'Gemini 1.5 Flash' },
                    { id: 'gemini-1.5-pro', name: 'Gemini 1.5 Pro' },
                ];
                if (!apiKey) return geminiFallback;
                try {
                    const res = await fetch('https://generativelanguage.googleapis.com/v1beta/models?key=' + apiKey);
                    if (!res.ok) return geminiFallback;
                    const data = await res.json();
                    const list = (data.models || [])
                        .filter(m => m.supportedGenerationMethods && m.supportedGenerationMethods.includes('generateContent'))
                        .map(m => ({ id: (m.name || '').replace('models/', ''), name: m.displayName || m.name }));
                    return list.length > 0 ? list : geminiFallback;
                } catch (e) { return geminiFallback; }
            }
            case 'mistral': {
                const mistralFallback = [
                    { id: 'mistral-small-latest', name: 'Mistral Small' },
                    { id: 'mistral-medium-latest', name: 'Mistral Medium' },
                    { id: 'mistral-large-latest', name: 'Mistral Large' },
                    { id: 'open-mistral-nemo', name: 'Mistral Nemo' },
                ];
                if (!apiKey) return mistralFallback;
                try {
                    const res = await fetch('https://api.mistral.ai/v1/models', {
                        headers: { 'Authorization': 'Bearer ' + apiKey },
                    });
                    if (!res.ok) return mistralFallback;
                    const data = await res.json();
                    const list = (data.data || []).map(m => ({ id: m.id, name: m.id }));
                    return list.length > 0 ? list : mistralFallback;
                } catch (e) { return mistralFallback; }
            }
            case 'cohere': {
                const cohereFallback = [
                    { id: 'command-r', name: 'Command R' },
                    { id: 'command-r-plus', name: 'Command R+' },
                    { id: 'command-r7b-12-2024', name: 'Command R 7B' },
                    { id: 'command-a-03-2025', name: 'Command A' },
                ];
                if (!apiKey) return cohereFallback;
                try {
                    const res = await fetch('https://api.cohere.com/v2/models', {
                        headers: { 'Authorization': 'Bearer ' + apiKey },
                    });
                    if (!res.ok) return cohereFallback;
                    const data = await res.json();
                    const list = (data.models || [])
                        .filter(m => m.endpoints && m.endpoints.includes('chat'))
                        .map(m => ({ id: m.name, name: m.name }));
                    return list.length > 0 ? list : cohereFallback;
                } catch (e) { return cohereFallback; }
            }
            case 'huggingface': {
                const hfFallback = [
                    { id: 'meta-llama/Llama-3.3-70B-Instruct', name: 'Llama 3.3 70B' },
                    { id: 'meta-llama/Llama-3.1-8B-Instruct', name: 'Llama 3.1 8B' },
                    { id: 'Qwen/Qwen2.5-72B-Instruct', name: 'Qwen 2.5 72B' },
                    { id: 'mistralai/Mistral-Nemo-Instruct-2407', name: 'Mistral Nemo' },
                    { id: 'microsoft/Phi-3.5-mini-instruct', name: 'Phi 3.5 Mini' },
                ];
                try {
                    const headers = {};
                    if (apiKey) headers['Authorization'] = 'Bearer ' + apiKey;
                    const res = await fetch('https://huggingface.co/api/models?pipeline_tag=text-generation&inference=warm&sort=trending&direction=-1&limit=50', { headers });
                    if (!res.ok) return hfFallback;
                    const data = await res.json();
                    const list = (Array.isArray(data) ? data : []).map(m => {
                        const id = m.id || m.modelId;
                        const shortName = id.split('/').pop();
                        return { id, name: shortName };
                    });
                    return list.length > 0 ? list : hfFallback;
                } catch (e) { return hfFallback; }
            }
            case 'custom':
            case 'custom-anthropic': {
                if (!apiKey || !baseUrl) return [];
                const urls = [
                    baseUrl.replace(/\/+$/, '') + '/models',
                    baseUrl.replace(/\/+$/, '') + '/v1/models',
                ];
                for (const u of urls) {
                    try {
                        const r = await fetch(u, { headers: { 'Authorization': 'Bearer ' + apiKey } });
                        if (!r.ok) continue;
                        const d = await r.json();
                        const list = d.data || d.models || (Array.isArray(d) ? d : []);
                        if (list.length > 0) {
                            return list.map(m => ({ id: m.id || m.model || m, name: m.name || m.id || m.model || String(m) }));
                        }
                    } catch (e) { /* skip */ }
                }
                return [];
            }
            default:
                return [];
        }
    } catch (err) {
        console.warn('Failed to fetch models for ' + type + ':', err);
        return [];
    }
}

// === استدعاء LLM عام مع دعم عدة مزودين ===
export async function callLLM(messages, provider, apiKeys, signal) {
    const type = provider.type || 'openrouter';
    const key = provider.requiresKey ? (apiKeys?.[type] || '') : (apiKeys?.[type] || '');

    switch (type) {
        case 'openrouter':
            if (!key) throw new Error('مفتاح OpenRouter مطلوب — سجل مجاناً في openrouter.ai');
            return callOpenAICompatible(
                provider.baseUrl || 'https://openrouter.ai/api/v1',
                provider.model,
                key,
                messages,
                signal
            );

        case 'groq':
            if (!key) throw new Error('مفتاح Groq مطلوب');
            return callOpenAICompatible(
                'https://api.groq.com/openai/v1',
                provider.model || 'llama-3.3-70b-versatile',
                key,
                messages,
                signal
            );

        case 'gemini':
            if (!key) throw new Error('مفتاح Gemini مطلوب');
            return callGemini(key, provider.model, messages, signal);

        case 'cohere':
            if (!key) throw new Error('مفتاح Cohere مطلوب');
            return callCohere(key, provider.model, messages, signal);

        case 'anthropic':
            if (!key) throw new Error('مفتاح Anthropic مطلوب');
            return callAnthropic(key, provider.model, messages, signal, provider.baseUrl);

        case 'custom-anthropic':
            if (!key && provider.requiresKey) throw new Error('مفتاح API مطلوب');
            return callAnthropic(key, provider.model, messages, signal, provider.baseUrl);

        case 'mistral':
            if (!key) throw new Error('مفتاح Mistral مطلوب');
            return callOpenAICompatible(
                'https://api.mistral.ai/v1',
                provider.model || 'mistral-small-latest',
                key,
                messages,
                signal
            );

        case 'huggingface':
            if (!key) throw new Error('توكن HuggingFace مطلوب');
            return callOpenAICompatible(
                'https://router.huggingface.co/hf-inference/models/' + (provider.model || 'meta-llama/Llama-3.2-3B-Instruct') + '/v1',
                provider.model || 'meta-llama/Llama-3.2-3B-Instruct',
                key,
                messages,
                signal
            );

        case 'custom':
            if (!key && provider.requiresKey) throw new Error('مفتاح API مطلوب');
            return callOpenAICompatible(
                provider.baseUrl,
                provider.model,
                key,
                messages,
                signal
            );

        default:
            throw new Error('نوع مزود غير معروف: ' + type);
    }
}

// === تحسين البرومبت — يجرب المزودين بالترتيب ===
export async function enhancePrompt(arabicText, llmProviders, apiKeys, systemPrompt, signal) {
    // فلترة المحتوى أولاً
    const safety = checkContentSafety(arabicText);
    if (!safety.safe) {
        return { success: false, blocked: true, reason: safety.reason, provider: 'فلتر الكلمات' };
    }

    const messages = [
        { role: 'system', content: systemPrompt || DEFAULT_SYSTEM_PROMPT },
        { role: 'user', content: arabicText },
    ];

    const enabledProviders = (llmProviders || [])
        .filter(p => p.enabled !== false)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    if (enabledProviders.length === 0) {
        return { success: false, blocked: false, reason: 'لا توجد خدمات LLM مفعلة', provider: '' };
    }

    const errors = [];
    for (const provider of enabledProviders) {
        try {
            const result = await callLLM(messages, provider, apiKeys, signal);

            // فحص ما إذا كان LLM حجب المحتوى
            if (result.startsWith('BLOCKED:')) {
                const reason = result.slice(8).trim() || 'محتوى غير مسموح به';
                return { success: false, blocked: true, reason, provider: provider.name };
            }

            return { success: true, prompt: result, provider: provider.name };
        } catch (err) {
            errors.push(provider.name + ': ' + err.message);
            console.warn('LLM provider failed:', provider.name, err);
        }
    }

    return {
        success: false,
        blocked: false,
        reason: 'فشلت جميع الخدمات:\n' + errors.join('\n'),
        provider: '',
    };
}

// === اختبار اتصال بمزود LLM ===
export async function testLLMProvider(provider, apiKeys) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);

    try {
        const result = await callLLM(
            [
                { role: 'system', content: 'Reply with exactly: OK' },
                { role: 'user', content: 'Test connection' },
            ],
            provider,
            apiKeys,
            controller.signal
        );
        clearTimeout(timeout);
        return { success: true, response: result.slice(0, 100) };
    } catch (err) {
        clearTimeout(timeout);
        return { success: false, error: err.message };
    }
}
